/**
 * 解析 SDF（プレーン OBB・三角柱・ブレースカプセル）とボクセル球トレース。
 * ヒット距離を返し、可視性は距離の有無で見る。
 */

import {
  ANALYTIC_BRACE_CAPACITY,
  ANALYTIC_BVH_BIN_PRIM_MAX,
  ANALYTIC_BVH_BINS,
  ANALYTIC_PLANE_CAPACITY,
  ANALYTIC_PLANE_MIN_HALF,
  ANALYTIC_PRIM_BRACE,
  ANALYTIC_PRIM_PLANE,
  ANALYTIC_PRIM_TRIANGLE,
  ANALYTIC_TRIANGLE_CAPACITY,
  SDF_TRACE_HIT_EPS,
  SDF_TRACE_SKIP,
} from '../shared/analytic-sdf';

/** uniforms: sdfMap, planeMap, triangleMap, braceMap, latticeOriginMap, planeCount, triangleCount, braceCount, sdfOrigin, sdfExtent, sdfVoxelSize, analyticSdf, analyticBvh, voxelSpheres */
export function sdfTraceGlsl(): string {
  return /* glsl */ `
uniform float analyticSdf;
uniform float analyticBvh;
uniform float voxelSpheres;
const int PLANE_CAPACITY = ${ANALYTIC_PLANE_CAPACITY};
const int TRIANGLE_CAPACITY = ${ANALYTIC_TRIANGLE_CAPACITY};
const int BRACE_CAPACITY = ${ANALYTIC_BRACE_CAPACITY};
const int BVH_BINS = ${ANALYTIC_BVH_BINS};
const int BVH_BIN_PRIM_MAX = ${ANALYTIC_BVH_BIN_PRIM_MAX};
const int PRIM_PLANE = ${ANALYTIC_PRIM_PLANE};
const int PRIM_TRIANGLE = ${ANALYTIC_PRIM_TRIANGLE};
const int PRIM_BRACE = ${ANALYTIC_PRIM_BRACE};

bool intersectAabb(vec3 origin, vec3 direction, vec3 bmin, vec3 bmax, out float tEnter, out float tExit) {
	vec3 inv = 1.0 / direction;
	vec3 t0 = (bmin - origin) * inv;
	vec3 t1 = (bmax - origin) * inv;
	vec3 tmin = min(t0, t1);
	vec3 tmax = max(t0, t1);
	tEnter = max(max(tmin.x, tmin.y), tmin.z);
	tExit = min(min(tmax.x, tmax.y), tmax.z);
	return tExit >= max(tEnter, 0.0);
}

bool intersectObb(
	vec3 origin, vec3 direction,
	vec3 center, vec3 halfExt, vec3 ax, vec3 ay, vec3 az,
	out float tEnter, out float tExit
) {
	vec3 p = origin - center;
	vec3 o = vec3(dot(p, ax), dot(p, ay), dot(p, az));
	vec3 d = vec3(dot(direction, ax), dot(direction, ay), dot(direction, az));
	vec3 h = max(halfExt, vec3(${ANALYTIC_PLANE_MIN_HALF.toFixed(4)}));
	return intersectAabb(o, d, -h, h, tEnter, tExit);
}

bool fetchAnalyticPlane(int i, out vec3 center, out vec3 halfExt, out vec3 ax, out vec3 ay, out vec3 az) {
	if (float(i) >= planeCount) return false;
	vec4 a = texelFetch(planeMap, ivec2(i, 0), 0);
	vec4 b = texelFetch(planeMap, ivec2(i, 1), 0);
	vec4 c = texelFetch(planeMap, ivec2(i, 2), 0);
	vec4 e = texelFetch(planeMap, ivec2(i, 3), 0);
	center = a.xyz;
	halfExt = max(vec3(a.w, b.xy), vec3(${ANALYTIC_PLANE_MIN_HALF.toFixed(4)}));
	ax = vec3(b.zw, c.x);
	ay = c.yzw;
	az = e.xyz;
	return true;
}

float analyticPlaneHitT(int i, vec3 origin, vec3 direction, float tMin, float tMax) {
	vec3 center;
	vec3 halfExt;
	vec3 ax;
	vec3 ay;
	vec3 az;
	if (!fetchAnalyticPlane(i, center, halfExt, ax, ay, az)) return -1.0;
	float t0;
	float t1;
	if (!intersectObb(origin, direction, center, halfExt, ax, ay, az, t0, t1)) return -1.0;
	if (t1 <= tMin || t0 >= tMax) return -1.0;
	return max(t0, tMin);
}

bool fetchAnalyticTriangle(int i, out vec3 a, out vec3 b, out vec3 c, out float halfT) {
	if (float(i) >= triangleCount) return false;
	vec4 ta = texelFetch(triangleMap, ivec2(i, 0), 0);
	vec4 tb = texelFetch(triangleMap, ivec2(i, 1), 0);
	vec4 tc = texelFetch(triangleMap, ivec2(i, 2), 0);
	a = ta.xyz;
	halfT = max(ta.w, ${ANALYTIC_PLANE_MIN_HALF.toFixed(4)});
	b = tb.xyz;
	c = tc.xyz;
	return true;
}

void rayTriangleInterval(vec3 ro, vec3 rd, vec3 a, vec3 b, vec3 c, inout float t0, inout float t1) {
	vec3 e1 = b - a;
	vec3 e2 = c - a;
	vec3 p = cross(rd, e2);
	float det = dot(e1, p);
	if (abs(det) < 1e-12) return;
	float inv = 1.0 / det;
	vec3 t = ro - a;
	float u = dot(t, p) * inv;
	if (u < 0.0 || u > 1.0) return;
	vec3 q = cross(t, e1);
	float v = dot(rd, q) * inv;
	if (v < 0.0 || u + v > 1.0) return;
	float hit = dot(e2, q) * inv;
	t0 = min(t0, hit);
	t1 = max(t1, hit);
}

float analyticTriangleHitT(int i, vec3 origin, vec3 direction, float tMin, float tMax) {
	vec3 a;
	vec3 b;
	vec3 c;
	float halfT;
	if (!fetchAnalyticTriangle(i, a, b, c, halfT)) return -1.0;
	vec3 bmin = min(min(a, b), c) - vec3(halfT);
	vec3 bmax = max(max(a, b), c) + vec3(halfT);
	float aabb0;
	float aabb1;
	if (!intersectAabb(origin, direction, bmin, bmax, aabb0, aabb1)) return -1.0;
	if (aabb1 <= tMin || aabb0 >= tMax) return -1.0;
	vec3 n = cross(b - a, c - a);
	float nlen = length(n);
	if (nlen < 1e-12) return -1.0;
	n /= nlen;
	vec3 off = n * halfT;
	vec3 a0 = a + off;
	vec3 b0 = b + off;
	vec3 c0 = c + off;
	vec3 a1 = a - off;
	vec3 b1 = b - off;
	vec3 c1 = c - off;
	float t0 = 1e30;
	float t1 = -1e30;
	rayTriangleInterval(origin, direction, a0, b0, c0, t0, t1);
	rayTriangleInterval(origin, direction, a1, c1, b1, t0, t1);
	rayTriangleInterval(origin, direction, a0, b0, b1, t0, t1);
	rayTriangleInterval(origin, direction, a0, b1, a1, t0, t1);
	rayTriangleInterval(origin, direction, b0, c0, c1, t0, t1);
	rayTriangleInterval(origin, direction, b0, c1, b1, t0, t1);
	rayTriangleInterval(origin, direction, c0, a0, a1, t0, t1);
	rayTriangleInterval(origin, direction, c0, a1, c1, t0, t1);
	if (t1 <= tMin || t0 >= tMax) return -1.0;
	return max(t0, tMin);
}

bool fetchAnalyticBrace(int i, out vec3 a, out vec3 b, out float r) {
	if (float(i) >= braceCount) return false;
	vec4 s = texelFetch(braceMap, ivec2(i, 0), 0);
	vec4 e = texelFetch(braceMap, ivec2(i, 1), 0);
	a = s.xyz;
	r = s.w;
	b = e.xyz;
	return true;
}

float rayCapsule(vec3 ro, vec3 rd, vec3 pa, vec3 pb, float ra) {
	vec3 ba = pb - pa;
	vec3 oa = ro - pa;
	float baba = dot(ba, ba);
	float bard = dot(ba, rd);
	float baoa = dot(ba, oa);
	float rdoa = dot(rd, oa);
	float oaoa = dot(oa, oa);
	float a = baba - bard * bard;
	float b = baba * rdoa - baoa * bard;
	float c = baba * oaoa - baoa * baoa - ra * ra * baba;
	float h = b * b - a * c;
	if (h < 0.0) return -1.0;
	float t = (-b - sqrt(h)) / max(a, 1e-8);
	float y = baoa + t * bard;
	if (y > 0.0 && y < baba) return t;
	vec3 oc = (y <= 0.0) ? oa : ro - pb;
	b = dot(rd, oc);
	c = dot(oc, oc) - ra * ra;
	h = b * b - c;
	if (h < 0.0) return -1.0;
	return -b - sqrt(h);
}

float analyticBraceHitT(int i, vec3 origin, vec3 direction, float tMin, float tMax) {
	if (tMax <= tMin) return -1.0;
	vec3 a;
	vec3 b;
	float r;
	if (!fetchAnalyticBrace(i, a, b, r)) return -1.0;
	float t = rayCapsule(origin, direction, a, b, r);
	return t >= tMin && t <= tMax ? t : -1.0;
}

float analyticPrimHitT(int kind, int idx, vec3 origin, vec3 direction, float tMin, float tMax) {
	if (kind == PRIM_PLANE) return analyticPlaneHitT(idx, origin, direction, tMin, tMax);
	if (kind == PRIM_TRIANGLE) return analyticTriangleHitT(idx, origin, direction, tMin, tMax);
	if (kind == PRIM_BRACE) return analyticBraceHitT(idx, origin, direction, tMin, tMax);
	return -1.0;
}

float analyticBvhHitT(vec3 origin, vec3 direction, float tMin, float tMax, bool anyHit) {
	float best = 1e30;
	for (int bin = 0; bin < BVH_BINS; bin++) {
		vec4 a = texelFetch(latticeOriginMap, ivec2(bin, 1), 0);
		vec4 b = texelFetch(latticeOriginMap, ivec2(bin, 2), 0);
		if (b.w < 0.5) continue;
		float t0;
		float t1;
		if (!intersectAabb(origin, direction, a.xyz, b.xyz, t0, t1)) continue;
		if (t1 < tMin || t0 > tMax || t0 >= best) continue;
		int start = int(a.w + 0.5);
		int n = int(min(b.w, float(BVH_BIN_PRIM_MAX)) + 0.5);
		for (int i = 0; i < BVH_BIN_PRIM_MAX; i++) {
			if (i >= n) break;
			vec4 prim = texelFetch(latticeOriginMap, ivec2(start + i, 3), 0);
			float t = analyticPrimHitT(int(prim.x + 0.5), int(prim.y + 0.5), origin, direction, tMin, tMax);
			if (t < 0.0) continue;
			if (anyHit) return t;
			best = min(best, t);
			tMax = min(tMax, t);
		}
	}
	return best < 1e29 ? best : -1.0;
}

float analyticLinearHitT(vec3 origin, vec3 direction, float tMin, float tMax, bool anyHit) {
	float best = 1e30;
	for (int i = 0; i < PLANE_CAPACITY; i++) {
		if (float(i) >= planeCount) break;
		float t = analyticPlaneHitT(i, origin, direction, tMin, tMax);
		if (t < 0.0) continue;
		if (anyHit) return t;
		best = min(best, t);
		tMax = min(tMax, t);
	}
	for (int i = 0; i < TRIANGLE_CAPACITY; i++) {
		if (float(i) >= triangleCount) break;
		float t = analyticTriangleHitT(i, origin, direction, tMin, tMax);
		if (t < 0.0) continue;
		if (anyHit) return t;
		best = min(best, t);
		tMax = min(tMax, t);
	}
	for (int i = 0; i < BRACE_CAPACITY; i++) {
		if (float(i) >= braceCount) break;
		float t = analyticBraceHitT(i, origin, direction, tMin, tMax);
		if (t < 0.0) continue;
		if (anyHit) return t;
		best = min(best, t);
		tMax = min(tMax, t);
	}
	return best < 1e29 ? best : -1.0;
}

float sampleVoxelSdf(vec3 p) {
	vec3 uvw = (p - sdfOrigin) / sdfExtent;
	if (any(lessThan(uvw, vec3(0.0))) || any(greaterThanEqual(uvw, vec3(1.0)))) return sdfExtent;
	return texture(sdfMap, uvw).r;
}

float surfaceBias() {
	return max(0.04, sdfVoxelSize * 0.75);
}

float voxelSphereHitT(vec3 origin, vec3 direction, float t, float tMax, float maxSteps) {
	float skip = t + ${SDF_TRACE_SKIP.toFixed(3)};
	float hitEps = ${SDF_TRACE_HIT_EPS.toFixed(3)};
	float startD = max(sampleVoxelSdf(origin), skip - t);
	for (int step = 0; step < 64; step++) {
		if (float(step) >= maxSteps) break;
		if (t >= tMax) return -1.0;
		float d = sampleVoxelSdf(origin + direction * t);
		if (d < hitEps && d < startD - skip * 0.5 && (t > skip || d < 0.0)) return t;
		t += max(d, hitEps);
	}
	return -1.0;
}

float traceSdfHitEx(vec3 origin, vec3 direction, float maxDistance, float maxSteps, bool anyHit) {
	vec3 bmin = sdfOrigin;
	vec3 bmax = sdfOrigin + vec3(sdfExtent);
	float tEnter;
	float tExit;
	if (!intersectAabb(origin, direction, bmin, bmax, tEnter, tExit)) return -1.0;
	float t = max(tEnter, 0.0);
	float tMax = min(tExit, maxDistance);
	if (t >= tMax) return -1.0;
	float hitEps = ${SDF_TRACE_HIT_EPS.toFixed(3)};
	float tMin = t + hitEps;
	float best = 1e30;
	float tAnalytic = analyticSdf < 0.5
		? -1.0
		: analyticBvh > 0.5
			? analyticBvhHitT(origin, direction, tMin, tMax, anyHit)
			: analyticLinearHitT(origin, direction, tMin, tMax, anyHit);
	if (tAnalytic >= 0.0) {
		if (anyHit) return tAnalytic;
		best = tAnalytic;
		tMax = min(tMax, tAnalytic);
	}
	float tVoxel = voxelSpheres > 0.5 ? voxelSphereHitT(origin, direction, t, tMax, maxSteps) : -1.0;
	if (tVoxel >= 0.0) best = min(best, tVoxel);
	return best < 1e29 ? best : -1.0;
}

float traceSdfHit(vec3 origin, vec3 direction, float maxDistance, float maxSteps) {
	return traceSdfHitEx(origin, direction, maxDistance, maxSteps, false);
}

float traceSdfAnyHit(vec3 origin, vec3 direction, float maxDistance, float maxSteps) {
	return traceSdfHitEx(origin, direction, maxDistance, maxSteps, true);
}
`;
}
